/**
 * Organization Model
 * Matches the Hub Organization data model structure
 * Reference: /american-drone-network-hub/src/data-models/organization.ts
 */

/**
 * Business Type Enum
 * Defines the platform role of the organization (Drone Service Provider vs Farmer).
 * Stored in the `business_type` column.  Separate from `organizationType` which
 * captures the legal entity structure (LLC, S-Corp, Individual, etc.).
 */
export enum BusinessType {
  DroneServiceProvider = 'Drone Service Provider',
  Farmer = 'Farmer',
}

export enum OrganizationType {
  Individual = 'individual',
  Llc = 'llc',
  SCorp = 's-corp',
  CCorp = 'c-corp',
  Partnership = 'partnership',
  SoleProprietorship = 'sole-proprietorship',
  NonProfit = 'non-profit',
  Other = 'other',
}

/**
 * Display labels for each OrganizationType.  Typed as a full Record so adding
 * a new enum member fails to compile until it has a label.
 */
export const ORGANIZATION_TYPE_LABELS: Record<OrganizationType, string> = {
  [OrganizationType.Individual]: 'Individual',
  [OrganizationType.Llc]: 'LLC',
  [OrganizationType.SCorp]: 'S-Corp',
  [OrganizationType.CCorp]: 'C-Corp',
  [OrganizationType.Partnership]: 'Partnership',
  [OrganizationType.SoleProprietorship]: 'Sole Proprietorship',
  [OrganizationType.NonProfit]: 'Non-Profit',
  [OrganizationType.Other]: 'Other',
};

/**
 * Dropdown options for organization type selects
 */
export const ORGANIZATION_TYPE_OPTIONS: { value: OrganizationType; label: string }[] =
  Object.values(OrganizationType).map(value => ({ value, label: ORGANIZATION_TYPE_LABELS[value] }));

/**
 * Returns the display label for an organization type, falling back to the raw
 * value for anything the API sends that isn't in the enum.
 */
export function getOrganizationTypeLabel(type: OrganizationType | string | null | undefined): string {
  if (!type) return '';
  return ORGANIZATION_TYPE_LABELS[type as OrganizationType] ?? type;
}

/**
 * Billing Responsibility Enum
 * Determines who pays for organization's services
 */
export type BillingResponsibility = 'self' | 'parent';

/**
 * Organization interface matching Hub Organization model
 * All fields use camelCase as per frontend conventions
 */
export interface Organization {
  // Base fields (from ModelBase)
  id: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt?: Date | string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  deletedBy?: string | null;

  // Basic Information
  name: string;
  organizationType?: OrganizationType | null;
  businessType?: BusinessType | null;

  // Owner Relationship
  ownerProfileId: string;

  // Tax Identifiers (Phase 1: plaintext, Phase 2: encrypted)
  ein?: string | null;
  ssn?: string | null;

  // Address Fields
  streetAddressLine1: string;
  streetAddressLine2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;

  // Geocoding (future use)
  latitude?: number | null;
  longitude?: number | null;

  // Hierarchical Structure
  parentOrganizationId?: string | null;

  // Billing Configuration
  billingResponsibility: BillingResponsibility;
  stripeCustomerId?: string | null;
  paymentMethodId?: string | null;

  // Optional Contact Info
  email?: string | null;
  phone?: string | null;
}

/**
 * Partial organization for updates
 */
export type OrganizationUpdate = Partial<Organization>;

/**
 * Organization creation payload
 */
export interface OrganizationCreate {
  name: string;
  organizationType?: OrganizationType;
  businessType?: BusinessType;
  ownerProfileId: string;
  streetAddressLine1: string;
  streetAddressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  ein?: string;
  ssn?: string;
  billingResponsibility?: BillingResponsibility;
  parentOrganizationId?: string;
  email?: string;
  phone?: string;
}
